import { Place, LocalShipping, Payment as PaymentIcon } from '@mui/icons-material';
import { TextField, FormControl, Typography, List, ListItem, ListItemIcon, FormControlLabel, Radio, RadioGroup, Grid2, Button, Box, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ShippingMethod } from '../../../services/companies';
import { Currencies, CURRENCY_SYMBOLS } from '../../../types';
import paypalLogo from '../../../assets/payment/paypal.png';
import paypalLogoDark from '../../../assets/payment/paypal-dark.png';
import mastercardLogo from '../../../assets/payment/mastercard.png';
import visaLogo from '../../../assets/payment/visa.webp';
import googlePayLogo from '../../../assets/payment/google-pay.png';

interface Props {
	onSubmit: (shippingInfo: ShippingInfo) => void;
	shippingMethods: ShippingMethod[];
	onShippingMethodChange: (shippingMethod: ShippingMethod) => void;
	shippingMethodDefaultValue: number;
	isSubmitting: boolean;
	country: string | null;
	currency: Currencies;
}

export interface ShippingInfo {
    customerEmail: string;
    phone: string;
	customerFirstName: string;
    customerLastName: string;
	customerAddress1: string;
	customerAddress2: string;
	customerCity: string;
	customerState: string;
	customerPostalCode: string;
	customerCountry: string;
}

const ShippingInfoForm = ({ onSubmit, shippingMethods, onShippingMethodChange, shippingMethodDefaultValue, isSubmitting, country, currency }: Props) => {
	const { t } = useTranslation();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const formData = new FormData(e.target as HTMLFormElement);
		const shippingInfo: ShippingInfo = {
			customerEmail: formData.get('email') as string,
			phone: formData.get('phone') as string,
			customerFirstName: formData.get('firstName') as string,
			customerLastName: formData.get('lastName') as string,
			customerAddress1: formData.get('addressLine1') as string,
			customerAddress2: formData.get('addressLine2') as string,
			customerCity: formData.get('city') as string,
			customerState: formData.get('state') as string,
			customerCountry: formData.get('country') as string,
			customerPostalCode: formData.get('postalCode') as string,
		};
		onSubmit(shippingInfo);
	};

	const theme = useTheme();

	return (
		<form onSubmit={handleSubmit}>
			<List>
				<Typography variant='h6' sx={{ my: 2 }} textAlign='center'>{t('checkout.customer')}</Typography>
				<ListItem>
					<ListItemIcon>
						<Place />
					</ListItemIcon>
					<Grid2 container spacing={2}>
						<Grid2 size={12}>
							<Typography variant='body2' color='text.secondary'>
								{t('checkout.contactInformation')}
							</Typography>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.firstName')}
								name="firstName"
								required
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.lastName')}
								name="lastName"
								required
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								type="email"
								name="email"
								label={t('checkout.email')}
								fullWidth
								required
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								type="tel"
								name="phone"
								label={t('checkout.phone')}
								fullWidth
								required
							/>
						</Grid2>
						<Grid2 size={12}>
							<Typography variant='body2' color='text.secondary'>
								{t('checkout.shippingAddress')}
							</Typography>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.addressLine1')}
								name="addressLine1"
								required
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.addressLine2')}
								name="addressLine2"
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.city')}
								name="city"
								required
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.state')}
								name="state"
								required
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.postalCode')}
								name="postalCode"
								required
							/>
						</Grid2>
						<Grid2 size={6}>
							<TextField
								fullWidth
								label={t('checkout.country')}
								name="country"
								required
								defaultValue={country ?? undefined}
							/>
						</Grid2>
					</Grid2>
				</ListItem>
				<Typography variant='h6' sx={{ mt: 3, mb: 2 }} textAlign='center'>{t('checkout.shippingInformation')}</Typography>
				<ListItem>
					<ListItemIcon>
						<LocalShipping />
					</ListItemIcon>
					<FormControl fullWidth>
						<RadioGroup
							// row
							aria-labelledby="shipping-method-label"
							name="shippingMethod"
							defaultValue={shippingMethodDefaultValue}
						>
							{shippingMethods.map((shippingMethod) => (<div key={shippingMethod.id}>
								<Box display='flex' flexDirection='row' alignItems='center' justifyContent='space-between'>
									<FormControlLabel 
										value={shippingMethod.id}
										control={<Radio />}
										label={shippingMethod.variant ? `${t(`shippingMethods.${shippingMethod.method}`)} (${shippingMethod.variant})` : t(`shippingMethods.${shippingMethod.method}`)}
										// disabled={shippingMethod.disabled} 
										onChange={() => onShippingMethodChange(shippingMethod)}
									/>
									{Number(shippingMethod.price) > 0 ? <Typography variant='caption' color='text.secondary'>
										+{Number(shippingMethod.price).toFixed(2)} {CURRENCY_SYMBOLS[currency]}
									</Typography> : <Typography variant='caption' color='text.secondary'>
										{t('checkout.noCharge')}
									</Typography>}
								</Box>
								{shippingMethod.description && <Typography variant='body2' color='text.secondary' mt={0}>
									{shippingMethod.description}
								</Typography>}
							</div>))}
						</RadioGroup>
					</FormControl>
				</ListItem>
			</List>
			<Box display='flex' justifyContent='center' mt={3} flexDirection='column' alignItems='center' gap={3}>
				{/* <div> */}
				<Button 
					fullWidth
					size='large' 
					variant='contained' 
					type="submit"
					disabled={isSubmitting}
					startIcon={<PaymentIcon />}
					sx={{py:2}}
				>
					{t('checkout.submitShippingInfo')}
				</Button>
				{/* </div> */}
				{theme.palette.mode !== 'dark' ? <Box display='flex' flexDirection='row' gap={2} alignItems='center'>
					<img src={paypalLogo} height={24} alt="PayPal Logo" />
					<img src={mastercardLogo} height={24} alt="Mastercard Logo" />
					<img src={visaLogo} height={24} alt="Visa Logo" />
					<img src={googlePayLogo} height={24} alt="Google Pay Logo" />
				</Box>
					:<Box display='flex' flexDirection='row' gap={2} alignItems='center'>
						<img src={paypalLogoDark} height={24} alt="PayPal Logo" />
						<img src={mastercardLogo} height={24} alt="Mastercard Logo" />
						<img src={visaLogo} height={24} alt="Visa Logo" />
						<img src={googlePayLogo} height={24} alt="Google Pay Logo" />
					</Box>}
			</Box>
		</form>
	);
};

export default ShippingInfoForm;
